import React, { useState } from "react";
import { TSubcase } from "../data-access/types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Autocomplete,
  TextField,
  DialogActions,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useGetDiff from "../data-access/useGetDiff";
import ChipMessage from "src/shared/components/ChipMessage";
import useGetStatusTitle from "../data-access/useGetStatusTitle";
import CaseAccordionDetails from "./CaseAccordionDetails";
import { useTranslation } from "react-i18next";
import InfoMessage from "src/shared/components/InfoMessage";
import { LoadingButton } from "@mui/lab";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { format } from "date-fns";
import useFinalizeSubcase from "../data-access/useFinalizeSubcase";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { MessageVariant } from "src/shared/enums/messageTypes/messageTypes";
import CancelIcon from "@mui/icons-material/Cancel";

export default function CaseAccordion({
  subcase,
  isCaseResponsible,
}: {
  subcase: TSubcase;
  isCaseResponsible: Boolean;
}) {
  const { t } = useTranslation();

  const finishOptionMap: Record<"2" | "3", string> = {
    "2": t("Ολοκλήρωση εργασίας με αλλαγή"),
    "3": t("Ολοκλήρωση εργασίας χωρίς αλλαγή"),
  };

  const [openFinishModal, setOpenFinishModal] = useState<boolean>(false);

  const statusTitle = useGetStatusTitle(subcase.status);

  const [expanded, setExpanded] = useState<boolean>(false);

  const [finishChoice, setFinishChoice] = useState<"2" | "3" | null>(null);
  const [finishMessage, setFinishMessage] = useState<string>("");

  const canExpand =
    !!isCaseResponsible && (subcase.isAuto ? subcase.status != 0 : true);

  const diffShouldBeCalculated =
    !subcase.isAuto && [0, 2, 3].includes(subcase.status);

  const { data, isLoading } = useGetDiff({
    subcase_id: subcase.subcase_id,
    enabled: !!expanded && diffShouldBeCalculated,
    setExpanded,
  });

  const { mutate: finalizeSubcase, isLoading: isLoadingFinalizingSubcase } =
    useFinalizeSubcase(subcase.subcase_id, setOpenFinishModal);

  const handleFinalizeSubcase = () => {
    if (finishChoice) {
      finalizeSubcase({
        subcaseId: subcase.subcase_id,
        decisionStatus: finishChoice,
        decisionComments: finishMessage,
      });
    }
  };

  return (
    <>
      <Accordion
        elevation={0}
        disableGutters
        sx={{
          padding: ".5rem",
          borderTop: 1,
          borderColor: "divider",
          pointerEvents: canExpand ? "auto" : "none",
        }}
        expanded={expanded}
        onChange={() => {
          if (canExpand) {
            setExpanded((previous) => !previous);
          }
        }}
      >
        <AccordionSummary
          expandIcon={
            canExpand ? (
              isLoading ? (
                <CircularProgress size={"1.5rem"} />
              ) : (
                <ExpandMoreIcon />
              )
            ) : null
          }
        >
          <Grid
            container
            sx={{
              alignItems: "start",
              width: "100%",
            }}
          >
            <Grid item xs={5} sx={{ display: "flex", alignItems: "start" }}>
              <Box sx={{ display: "flex", gap: ".4rem", alignItems: "center" }}>
                {subcase.priority == 1 && (
                  <Tooltip
                    title={t(
                      "Αυτή η αναντιστοιχία θεωρείται υψηλής προτεραιότητας",
                    )}
                  >
                    <WarningAmberRoundedIcon sx={{ color: "#ED6C01" }} />
                  </Tooltip>
                )}
                <Typography variant="body1" fontSize="1rem">
                  {subcase.title}
                </Typography>
              </Box>
            </Grid>
            <Grid
              item
              xs={2}
              sx={{
                display: "flex",
                justifyContent: "center",
                flex: 1,
                alignItems: "start",
              }}
            >
              <ChipMessage
                message={statusTitle.title}
                variant={statusTitle.variant}
              />
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                width: "100%",
                minHeight: "10rem",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <>
              {data?.data.diff && diffShouldBeCalculated && (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <CaseAccordionDetails
                    subcase={subcase}
                    diff={data?.data.diff}
                  />
                  {subcase.status === 0 && (
                    <Button
                      variant="contained"
                      color="warning"
                      sx={{ width: "20rem" }}
                      onClick={() => setOpenFinishModal(true)}
                    >
                      {t("Ολοκλήρωση Εργασίας")}
                    </Button>
                  )}
                </Box>
              )}
              {!diffShouldBeCalculated && (
                <Box
                  sx={{
                    p: 1,
                    mt: 2,
                    minHeight: "10rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {subcase.isAuto && subcase.status === 3 ? (
                    <>
                      <CheckCircleOutlineIcon
                        sx={{ height: "5rem", width: "5rem", color: "orange" }}
                      />
                      <Typography variant="subtitle1" sx={{ color: "grey" }}>
                        {t(
                          "Δεν πραγματοποιήθηκε ενημέρωση του μητρώου του Γ.Ε.ΜΗ. από το μητρώο της ΑΑΔΕ.",
                        )}
                      </Typography>
                    </>
                  ) : subcase.isAuto && subcase.status !== 4 ? (
                    <>
                      <CheckCircleOutlineIcon
                        sx={{ height: "5rem", width: "5rem", color: "green" }}
                      />
                      <Typography variant="subtitle1" sx={{ color: "grey" }}>
                        {t(
                          "Το μητρώο του Γ.Ε.ΜΗ. ενημερώθηκε αυτόματα από το μητρώου της ΑΑΔΕ",
                        )}
                      </Typography>
                    </>
                  ) : subcase.status === 4 ? (
                    <>
                      <CancelIcon
                        sx={{ height: "5rem", width: "5rem", color: "red" }}
                      />
                      <Typography variant="subtitle1" sx={{ color: "grey" }}>
                        {t(
                          "Η εταιρεία είναι σε κατάσταση διαγραφής και όλες οι επιμέρους αναντιστοιχίες ακυρώθηκαν",
                        )}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CheckCircleOutlineIcon
                        sx={{ height: "5rem", width: "5rem", color: "green" }}
                      />
                      <Typography variant="subtitle1" sx={{ color: "grey" }}>
                        {t(
                          "Η αναντιστοιχία ολοκληρώθηκε με πλήρη ταύτιση προς τις τιμές τις ΑΑΔΕ",
                        )}
                      </Typography>
                    </>
                  )}
                </Box>
              )}
              {subcase.status != 0 && (
                <Typography
                  variant="subtitle2"
                  sx={{ alignSelf: "flex-start", mt: 5 }}
                >
                  {t("Ημερομηνία Διεκπεραίωσης")}
                  {": "}
                  {format(
                    new Date(subcase.completed_date),
                    "dd/MM/yyyy HH:mm:ss",
                  )}
                </Typography>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>
      <Dialog
        open={openFinishModal}
        onClose={() => setOpenFinishModal(false)}
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5">{t("Ολοκλήρωση εργασίας")}</Typography>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              padding: "1rem",
            }}
          >
            <InfoMessage
              variant={MessageVariant.Info}
              message={t(
                "Έχετε επιλέξει να ολοκληρώσετε την συγκεκριμένη εργασία. Επιλέξτε ποια από τις παρακάτω είναι η αιτιολογία ολοκλήρωσης.",
              )}
            />
            <Autocomplete
              fullWidth
              options={["2", "3"]}
              getOptionLabel={(option: "2" | "3") => finishOptionMap[option]}
              isOptionEqualToValue={(option, value) => option === value}
              value={finishChoice}
              onChange={(e, value) => setFinishChoice(value)}
              renderInput={(params) => (
                <TextField
                  required
                  label={t("Αιτιολογία ολοκλήρωσης")}
                  {...params}
                />
              )}
            />
            <TextField
              label={t("Περιγραφή")}
              multiline
              rows={4}
              value={finishMessage}
              onChange={(e) => setFinishMessage(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: "1rem" }}>
          <Button
            variant="contained"
            color="error"
            onClick={() => setOpenFinishModal(false)}
          >
            {t("Ακύρωση")}
          </Button>
          <LoadingButton
            loading={isLoadingFinalizingSubcase}
            disabled={!finishChoice}
            variant="contained"
            color="primary"
            onClick={handleFinalizeSubcase}
          >
            {t("Αποθήκευση")}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
